import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { StarIcon, HeartIcon, EyeIcon, GlobeIcon, TwitterIcon, InstagramIcon, LinkedinIcon, GithubIcon, ExternalLinkIcon, CheckCircleIcon, MapPinIcon, CalendarIcon, PackageIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../components/auth/AuthProvider';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
// Sample seller data - in a real app, this would come from an API
const sellersData = {
  designmaster: {
    id: 'designmaster',
    name: 'DesignMaster',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    coverImage: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    bio: 'Award-winning designer specializing in website templates and UI kits with over 10 years of experience. Creating beautiful, functional designs that help businesses stand out.',
    location: 'San Francisco, CA',
    memberSince: 'January 2020',
    verified: true,
    rating: 4.9,
    reviewCount: 128,
    totalSales: 1240,
    social: {
      website: 'https://designmaster.com',
      twitter: 'designmaster',
      instagram: 'designmaster',
      linkedin: 'designmaster',
      github: 'designmaster'
    }
  },
  uigenius: {
    id: 'uigenius',
    name: 'UIGenius',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    coverImage: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    bio: 'UI/UX designer focused on creating beautiful, intuitive interfaces. Specializing in e-commerce UI kits and design systems that convert visitors into customers.',
    location: 'New York, NY',
    memberSince: 'March 2021',
    verified: true,
    rating: 4.8,
    reviewCount: 86,
    totalSales: 920,
    social: {
      website: 'https://uigenius.design',
      twitter: 'uigenius',
      instagram: 'uigenius.design',
      linkedin: null,
      github: null
    }
  },
  // ... other existing sellers ...
  // Add the demo seller IDs that might be used in the auth system
  'user-123': {
    id: 'user-123',
    name: 'Demo User',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    coverImage: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    bio: 'Demo seller account with premium digital products. Specializing in website templates and UI kits for modern businesses and creative professionals.',
    location: 'San Francisco, CA',
    memberSince: 'January 2023',
    verified: true,
    rating: 4.9,
    reviewCount: 42,
    totalSales: 350,
    social: {
      website: 'https://example.com',
      twitter: 'demouser',
      instagram: 'demouser',
      linkedin: 'demouser',
      github: 'demouser'
    }
  },
  'seller-123': {
    id: 'seller-123',
    name: 'Demo Seller',
    avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
    coverImage: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    bio: 'Digital product creator with a passion for clean, functional design. Creating templates and UI kits that help businesses succeed online.',
    location: 'New York, NY',
    memberSince: 'March 2023',
    verified: true,
    rating: 4.8,
    reviewCount: 36,
    totalSales: 280,
    social: {
      website: 'https://example.com',
      twitter: 'demoseller',
      instagram: 'demoseller',
      linkedin: 'demoseller',
      github: 'demoseller'
    }
  },
  'user-new': {
    id: 'user-new',
    name: 'New User',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    coverImage: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    bio: 'New seller on the platform creating high-quality digital products. Focused on mobile app UI kits and website templates.',
    location: 'Los Angeles, CA',
    memberSince: 'June 2023',
    verified: true,
    rating: 4.7,
    reviewCount: 12,
    totalSales: 85,
    social: {
      website: 'https://example.com',
      twitter: 'newuser',
      instagram: 'newuser',
      linkedin: 'newuser',
      github: null
    }
  }
};
// Sample product data - in a real app, this would come from an API
const products = [{
  id: 1,
  title: 'Premium Website Template',
  description: 'A modern, responsive website template perfect for creative professionals.',
  price: 49,
  category: 'Website Templates',
  image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.9,
  seller: 'DesignMaster',
  sellerId: 'designmaster',
  platform: 'HTML',
  licenseType: 'Commercial',
  useCase: 'Portfolio',
  format: 'ZIP',
  skillLevel: 'Intermediate',
  industry: 'Startups / SaaS',
  creatorType: 'Designer',
  isTrending: true,
  dateAdded: '2023-10-15'
}, {
  id: 2,
  title: 'E-commerce UI Kit',
  description: 'Complete UI kit for building modern e-commerce experiences.',
  price: 79,
  category: 'UI Kits',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.8,
  seller: 'UIGenius',
  sellerId: 'uigenius',
  platform: 'Figma',
  licenseType: 'Commercial',
  useCase: 'SaaS',
  format: '.fig',
  skillLevel: 'Advanced',
  industry: 'Startups / SaaS',
  creatorType: 'Designer',
  isNewArrival: true,
  dateAdded: '2023-11-01'
}, {
  id: 3,
  title: 'Social Media Pack',
  description: 'Professionally designed templates for all your social media needs.',
  price: 39,
  category: 'Social Media Content Packs',
  image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.7,
  seller: 'SocialPro',
  sellerId: 'socialpro',
  platform: 'Photoshop',
  licenseType: 'Personal',
  useCase: 'YouTube',
  format: 'PSD',
  skillLevel: 'Beginner',
  industry: 'Creators / Influencers',
  creatorType: 'Content Creator',
  isTrending: true,
  dateAdded: '2023-09-20'
}, {
  id: 4,
  title: 'Mobile App UI Kit',
  description: 'Comprehensive UI components for modern mobile applications.',
  price: 59,
  category: 'Mobile App Kits',
  image: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.9,
  seller: 'AppMaster',
  sellerId: 'appmaster',
  platform: 'Figma',
  licenseType: 'Commercial',
  useCase: 'Mobile App',
  format: '.fig',
  skillLevel: 'Intermediate',
  industry: 'AI & Tech',
  creatorType: 'Developer',
  dateAdded: '2023-08-12'
}, {
  id: 5,
  title: 'Dashboard Template',
  description: 'Clean and modern dashboard template with data visualization components.',
  price: 69,
  category: 'UI Kits',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.8,
  seller: 'DataViz',
  sellerId: 'dataviz',
  platform: 'React',
  licenseType: 'Extended',
  useCase: 'SaaS',
  format: 'ZIP',
  skillLevel: 'Advanced',
  industry: 'Finance',
  creatorType: 'Developer',
  dateAdded: '2023-07-28'
}, {
  id: 6,
  title: 'Creative Portfolio Template',
  description: 'Showcase your work with this modern portfolio template.',
  price: 39,
  category: 'Website Templates',
  image: 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.7,
  seller: 'DesignMaster',
  sellerId: 'designmaster',
  platform: 'HTML',
  licenseType: 'Personal',
  useCase: 'Portfolio',
  format: 'ZIP',
  skillLevel: 'Beginner',
  industry: 'Creators / Influencers',
  creatorType: 'Designer',
  dateAdded: '2023-09-05'
}, {
  id: 7,
  title: 'Startup Landing Page',
  description: 'Convert visitors into customers with this high-converting landing page.',
  price: 59,
  category: 'Website Templates',
  image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.8,
  seller: 'DesignMaster',
  sellerId: 'designmaster',
  platform: 'HTML',
  licenseType: 'Commercial',
  useCase: 'SaaS',
  format: 'ZIP',
  skillLevel: 'Intermediate',
  industry: 'Startups / SaaS',
  creatorType: 'Designer',
  isTrending: true,
  dateAdded: '2023-10-10'
}, {
  id: 8,
  title: 'E-commerce Product Showcase',
  description: 'Beautiful product showcase templates for your online store.',
  price: 49,
  category: 'UI Kits',
  image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.9,
  seller: 'UIGenius',
  sellerId: 'uigenius',
  platform: 'Figma',
  licenseType: 'Commercial',
  useCase: 'SaaS',
  format: '.fig',
  skillLevel: 'Intermediate',
  industry: 'Startups / SaaS',
  creatorType: 'Designer',
  dateAdded: '2023-09-15'
}, {
  id: 9,
  title: 'Instagram Story Templates',
  description: 'Stand out on Instagram with these professionally designed story templates.',
  price: 29,
  category: 'Social Media Content Packs',
  image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.6,
  seller: 'SocialPro',
  sellerId: 'socialpro',
  platform: 'Photoshop',
  licenseType: 'Personal',
  useCase: 'YouTube',
  format: 'PSD',
  skillLevel: 'Beginner',
  industry: 'Creators / Influencers',
  creatorType: 'Content Creator',
  dateAdded: '2023-08-25'
}, {
  id: 10,
  title: 'App Onboarding Screens',
  description: 'Improve user experience with these beautiful onboarding screen templates.',
  price: 39,
  category: 'Mobile App Kits',
  image: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  rating: 4.8,
  seller: 'AppMaster',
  sellerId: 'appmaster',
  platform: 'Figma',
  licenseType: 'Commercial',
  useCase: 'Mobile App',
  format: '.fig',
  skillLevel: 'Intermediate',
  industry: 'AI & Tech',
  creatorType: 'Developer',
  isNewArrival: true,
  dateAdded: '2023-10-20'
}];
// Product card component
const ProductCard = ({
  product
}: {
  product: any;
}) => {
  return <Link to={`/product/${product.id}`} className="group" data-id="element-5920">
      <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-id="element-5921">
        <div className="relative aspect-[4/3] overflow-hidden" data-id="element-5922">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover transition-transform duration-400 ease-in-out group-hover:scale-105" data-id="element-5923" />
          {/* Category tag */}
          <div className="absolute top-3 left-3" data-id="element-5924">
            <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-5925">
              {product.category}
            </span>
          </div>
          {/* Trending badge */}
          {product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-5926">
              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-5927">
                <span className="mr-1" data-id="element-5928">🔥</span>Trending
              </span>
            </div>}
          {/* New badge */}
          {product.isNewArrival && !product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-5929">
              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-5930">
                <span className="mr-1" data-id="element-5931">✨</span>New
              </span>
            </div>}
        </div>
        <div className="p-4" data-id="element-5932">
          <div className="flex items-center justify-between mb-1.5" data-id="element-5933">
            {/* Rating */}
            <div className="flex items-center" data-id="element-5934">
              <StarIcon className="h-3.5 w-3.5 text-yellow-400 fill-current" data-id="element-5935" />
              <span className="text-xs text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-5936">
                {product.rating}
              </span>
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-[#7A8F8A]" data-id="element-5937">
              ${product.price}
            </span>
          </div>
          <h3 className="text-base font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9] group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors line-clamp-1" data-id="element-5938">
            {product.title}
          </h3>
          <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-5939">
            {product.platform && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-5940">
                {product.platform}
              </span>}
            {product.skillLevel && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-5941">
                {product.skillLevel}
              </span>}
          </div>
        </div>
      </div>
    </Link>;
};
// Stats box component
const StatBox = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) => {
  return <div className="bg-white dark:bg-[#162825] p-4 rounded-xl shadow-sm flex flex-col items-center justify-center text-center" data-id="element-5942">
      <div className="text-[#2DB489] dark:text-[#4ED1A0] mb-2" data-id="element-5943">{icon}</div>
      <p className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5944">
        {value}
      </p>
      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5945">{label}</p>
    </div>;
};
export const SellerProfilePage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [seller, setSeller] = useState<any>(null);
  const [sellerProducts, setSellerProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [storeOnline, setStoreOnline] = useState<boolean | null>(null);
  const {
    sellerId
  } = useParams<{
    sellerId: string;
  }>();
  const {
    user
  } = useAuth();
  const isOwner = !!(user?.id && sellerId && user.id === sellerId);
  // Force scroll to top on mount and override browser scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);
  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Fetch seller data from Firestore, fallback to demo data
  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      return;
    }
    const fetchSellerData = async () => {
      try {
        // Try Firestore first
        const userDocRef = doc(db, 'users', sellerId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          // Map Firestore user fields to the seller profile shape
          const firestoreSeller = {
            id: sellerId,
            name: data.displayName || data.name || data.storeName || 'Seller',
            avatar: data.profileImage || data.avatar || data.avatarUrl || data.photoURL || 'https://randomuser.me/api/portraits/lego/1.jpg',
            coverImage: data.profileBanner || data.storeBanner || data.coverImage || '',
            bio: data.bio || data.about || data.storeDescription || 'This seller has not added a bio yet.',
            location: data.location || 'Location not set',
            memberSince: data.createdAt ? new Date(data.createdAt.seconds ? data.createdAt.seconds * 1000 : data.createdAt).toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            }) : 'Recently joined',
            verified: data.roles?.includes('seller') || data.isSeller || false,
            rating: data.rating || 0,
            reviewCount: data.reviewCount || 0,
            totalSales: data.totalSales || 0,
            publicUsername: data.publicUsername || '',
            social: {
              website: data.socialLinks?.website || data.website || null,
              twitter: data.socialLinks?.twitter || null,
              instagram: data.socialLinks?.instagram || null,
              linkedin: data.socialLinks?.linkedin || null,
              github: data.socialLinks?.github || null
            }
          };
          setSeller(firestoreSeller);
          // Capture store online status
          setStoreOnline(data.storeOnline !== undefined ? data.storeOnline : true);
          // Try to fetch real products from Firestore
          try {
            const productsRef = collection(db, 'products');
            const productsQuery = query(productsRef, where('sellerId', '==', sellerId));
            const productsSnapshot = await getDocs(productsQuery);
            if (!productsSnapshot.empty) {
              const firestoreProducts = productsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              setSellerProducts(firestoreProducts);
            } else {
              // No products in Firestore for this seller
              setSellerProducts([]);
            }
          } catch {
            // Products fetch failed, leave empty
            setSellerProducts([]);
          }
        } else {
          // Document doesn't exist in Firestore — try demo data fallback
          const demoSeller = sellersData[sellerId.toLowerCase() as keyof typeof sellersData];
          if (demoSeller) {
            setSeller(demoSeller);
            const filteredProducts = products.filter(product => product.sellerId.toLowerCase() === sellerId.toLowerCase());
            setSellerProducts(filteredProducts);
          }
          // If neither Firestore nor demo data found, seller stays null → "Seller Not Found"
        }
      } catch {
        // Firestore failed entirely (e.g., not configured) — fallback to demo data
        const demoSeller = sellersData[sellerId.toLowerCase() as keyof typeof sellersData];
        if (demoSeller) {
          setSeller(demoSeller);
          const filteredProducts = products.filter(product => product.sellerId.toLowerCase() === sellerId.toLowerCase());
          setSellerProducts(filteredProducts);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSellerData();
  }, [sellerId]);
  // Common layout wrapper for all states
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => <div className="w-full bg-white dark:bg-[#0F1F1D] min-h-screen" data-id="element-5946">
      <Navbar scrolled={scrolled} data-id="element-5947" />
      <main className="pt-24 pb-20" data-id="element-5948">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5949">
          <div className="max-w-6xl mx-auto" data-id="element-5950">{children}</div>
        </div>
      </main>
      <Footer data-id="element-5951" />
    </div>;
  if (loading) {
    return <PageLayout data-id="element-5952">
        <div className="animate-pulse" data-id="element-5953">
          <div className="h-64 bg-gray-200 dark:bg-[#162825] rounded-xl mb-8" data-id="element-5954"></div>
          <div className="flex flex-col md:flex-row gap-8" data-id="element-5955">
            <div className="md:w-1/3" data-id="element-5956">
              <div className="h-8 bg-gray-200 dark:bg-[#162825] rounded mb-4 w-3/4" data-id="element-5957"></div>
              <div className="h-4 bg-gray-200 dark:bg-[#162825] rounded mb-2 w-full" data-id="element-5958"></div>
              <div className="h-4 bg-gray-200 dark:bg-[#162825] rounded mb-2 w-full" data-id="element-5959"></div>
              <div className="h-4 bg-gray-200 dark:bg-[#162825] rounded mb-2 w-3/4" data-id="element-5960"></div>
            </div>
            <div className="md:w-2/3" data-id="element-5961">
              <div className="h-8 bg-gray-200 dark:bg-[#162825] rounded mb-6 w-1/2" data-id="element-5962"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5963">
                {[1, 2, 3, 4, 5, 6].map(item => <div key={item} className="bg-gray-200 dark:bg-[#162825] rounded-xl h-64" data-id="element-5964"></div>)}
              </div>
            </div>
          </div>
        </div>
      </PageLayout>;
  }
  if (!seller) {
    return <PageLayout data-id="element-5965">
        <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-5966">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5967">
            Seller Not Found
          </h1>
          <p className="text-gray-600 dark:text-[#B0C1BD] mb-8" data-id="element-5968">
            We couldn't find the seller you're looking for. They may have
            changed their username or no longer be active.
          </p>
          <Link to="/browse" className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-5969">
            Browse Products
          </Link>
        </div>
      </PageLayout>;
  }
  // Store offline: block non-owners
  if (storeOnline === false && !isOwner) {
    return <PageLayout data-id="element-5970">
        <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-5971">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-[#1A3330] flex items-center justify-center" data-id="element-5972">
            <PackageIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-5973" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5974">
            This store is currently offline
          </h1>
          <p className="text-gray-600 dark:text-[#B0C1BD] mb-8" data-id="element-5975">
            The seller has temporarily taken their store offline. Please check
            back later.
          </p>
          <Link to="/browse" className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-5976">
            Browse Products
          </Link>
        </div>
      </PageLayout>;
  }
  return <PageLayout data-id="element-5977">
      {/* Owner offline notice */}
      {isOwner && storeOnline === false && <div className="mb-6 flex items-center px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30" data-id="element-5978">
          <EyeIcon className="w-5 h-5 text-amber-500 dark:text-amber-400 mr-3 flex-shrink-0" data-id="element-5979" />
          <span className="text-sm text-amber-700 dark:text-amber-300" data-id="element-5980">
            Your store is offline — only you can see this page. Go to{' '}
            <Link to="/settings/store" className="underline font-medium hover:text-amber-800 dark:hover:text-amber-200" data-id="element-5981">
              Store Settings
            </Link>{' '}
            to bring it back online.
          </span>
        </div>}
      {/* Cover Image */}
      <div className="relative mb-6" data-id="element-5982">
        <div className="w-full h-36 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1A3330]" data-id="element-5983">
          {seller.coverImage ? <img src={seller.coverImage} alt="Banner" className="w-full h-full object-cover" data-id="element-5984" /> : <div className="w-full h-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#1A3330] dark:to-[#2DB489]" data-id="element-5985" />}
        </div>
        {/* Profile Image (positioned on the banner, matching Account Settings) */}
        <div className="absolute bottom-4 left-6" data-id="element-5986">
          <div className="relative" data-id="element-5987">
            <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white dark:border-[#162825] shadow-sm bg-white dark:bg-[#162825]" data-id="element-5988">
              <img src={seller.avatar} alt={seller.name} className="w-full h-full object-cover" data-id="element-5989" />
            </div>
            {seller.verified && <div className="absolute -bottom-1 -right-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white p-1 rounded-full border-2 border-white dark:border-[#162825]" data-id="element-5990">
                <CheckCircleIcon className="h-4 w-4" data-id="element-5991" />
              </div>}
          </div>
        </div>
      </div>
      {/* Seller Info */}
      <div className="text-center mb-12" data-id="element-5992">
        <h1 className="text-3xl md:text-4xl font-bold mb-1 text-gray-800 dark:text-[#F8FAF9]" data-id="element-5993">
          {seller.name}
        </h1>
        {seller.publicUsername && <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-2" data-id="element-5994">
            @{seller.publicUsername}
          </p>}
        <div className="flex items-center justify-center mb-4" data-id="element-5995">
          <div className="flex items-center mr-4" data-id="element-5996">
            <StarIcon className="h-5 w-5 text-yellow-400 fill-current" data-id="element-5997" />
            <span className="ml-1 text-gray-700 dark:text-[#B0C1BD] font-medium" data-id="element-5998">
              {seller.rating} ({seller.reviewCount} reviews)
            </span>
          </div>
          {seller.verified && <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5999">
              <CheckCircleIcon className="h-5 w-5 mr-1" data-id="element-6000" />
              <span className="font-medium" data-id="element-6001">Verified Seller</span>
            </div>}
        </div>
        <div className="flex items-center justify-center text-sm text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-6002">
          <div className="flex items-center mr-4" data-id="element-6003">
            <MapPinIcon className="h-4 w-4 mr-1" data-id="element-6004" />
            <span data-id="element-6005">{seller.location}</span>
          </div>
          <div className="flex items-center" data-id="element-6006">
            <CalendarIcon className="h-4 w-4 mr-1" data-id="element-6007" />
            <span data-id="element-6008">Member since {seller.memberSince}</span>
          </div>
        </div>
        <p className="max-w-2xl mx-auto text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-6009">
          {seller.bio}
        </p>
        {/* Social Links */}
        <div className="flex items-center justify-center space-x-4 mb-8" data-id="element-6010">
          {seller.social.website && <a href={seller.social.website} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-6011">
              <GlobeIcon className="h-5 w-5" data-id="element-6012" />
            </a>}
          {seller.social.twitter && <a href={`https://twitter.com/${seller.social.twitter}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-6013">
              <TwitterIcon className="h-5 w-5" data-id="element-6014" />
            </a>}
          {seller.social.instagram && <a href={`https://instagram.com/${seller.social.instagram}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-6015">
              <InstagramIcon className="h-5 w-5" data-id="element-6016" />
            </a>}
          {seller.social.linkedin && <a href={`https://linkedin.com/in/${seller.social.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-6017">
              <LinkedinIcon className="h-5 w-5" data-id="element-6018" />
            </a>}
          {seller.social.github && <a href={`https://github.com/${seller.social.github}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-6019">
              <GithubIcon className="h-5 w-5" data-id="element-6020" />
            </a>}
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-12" data-id="element-6021">
          <StatBox icon={<StarIcon className="h-6 w-6" data-id="element-6023" />} label="Rating" value={seller.rating} data-id="element-6022" />
          <StatBox icon={<ExternalLinkIcon className="h-6 w-6" data-id="element-6025" />} label="Total Sales" value={seller.totalSales.toLocaleString()} data-id="element-6024" />
          <StatBox icon={<PackageIcon className="h-6 w-6" data-id="element-6027" />} label="Products" value={sellerProducts.length} data-id="element-6026" />
        </div>
      </div>
      {/* Products Section */}
      <div className="mb-12" data-id="element-6028">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-[#F8FAF9]" data-id="element-6029">
          Products by {seller.name}
        </h2>
        {sellerProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-6030">
            {sellerProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-6031" />)}
          </div> : <div className="bg-white dark:bg-[#162825] rounded-xl p-8 text-center" data-id="element-6032">
            <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6033">
              This seller doesn't have any products available at the moment.
            </p>
          </div>}
      </div>
      {/* Reviews Section - Placeholder for future implementation */}
      <div data-id="element-6034">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-[#F8FAF9]" data-id="element-6035">
          Reviews ({seller.reviewCount})
        </h2>
        <div className="bg-white dark:bg-[#162825] rounded-xl p-8 text-center" data-id="element-6036">
          <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6037">No reviews yet.</p>
        </div>
      </div>
    </PageLayout>;
};